// Single source of truth for verbatim customer testimonials. Every
// entry is pulled directly from a public review surface. Shopify App
// Store, G2, so the quote is auditable, not invented. Photos are
// optional; we render initials avatars when missing.
//
// Used by /testimonials, /customers and the homepage Testimonials
// section. Keep this file synced with the actual review pages.

export type CustomerTestimonialSource = 'shopify' | 'g2' | 'idukki'

export type CustomerTestimonial = {
  source: CustomerTestimonialSource
  /** Brand name as it appears on the review surface. */
  brand: string
  /** Optional individual name (G2 + identifiable Shopify reviewers). */
  reviewer?: string
  /** Reviewer's role + company size, e.g. "CRO Specialist · Enterprise (> 1000 emp.)". */
  role?: string
  /** Region or country, e.g. "India". */
  region?: string
  /** Duration using the product, e.g. "9 months using the app". */
  duration?: string
  /** Publication date, formatted "May 8, 2026". */
  date: string
  /** Star rating, 1-5. Verbatim from the source. */
  rating?: number
  /** Optional review headline (G2 surfaces these). */
  title?: string
  /** Verbatim primary quote. */
  quote: string
  /** Verbatim pull-quote, a second strong sentence from the same review. */
  pull: string
  /** Reviewer photo if the source surfaced one. Initials are used otherwise. */
  photo?: string
}

export const customerTestimonials: CustomerTestimonial[] = [
  {
    source: 'shopify',
    brand: 'Malléwear',
    region: 'India',
    duration: '6 months using the app',
    date: 'July 29, 2026',
    rating: 5,
    quote:
      'It was a wonderful experience working with Sahil and the team at Idukki. They were extremely supportive throughout the process, responsive whenever we needed assistance, and made the implementation smooth and hassle-free. We truly appreciated their guidance and commitment, and we would highly recommend Idukki to any brand looking to build trust and improve conversions through UGC and shoppable video experiences.',
    pull: 'We would highly recommend Idukki to any brand looking to build trust and improve conversions through UGC and shoppable video experiences.',
  },
  {
    source: 'shopify',
    brand: 'COSSET CLOTHING',
    region: 'India',
    duration: '9 months using the app',
    date: 'May 8, 2026',
    rating: 5,
    quote:
      "We've been using Idukki – Shoppable Videos & UGC App for the past couple of months, and it's helped us finally make proper use of all our UGC and collaboration content across the website. Customers are able to see the natural flow and fit of the garments on women across different age groups, which has made the shopping experience feel far more real and relatable.",
    pull: 'For fashion brands sitting on a lot of creator/UGC content, this has been a very effective way to turn that content into something far more measurable and conversion focused.',
  },
  // Ritualistic is also a published case study (lib/copy/case-studies.ts,
  // slug 'ritualistic') and a live storefront (lib/copy/storefronts.ts) —
  // migrated to Idukki from Whatmore, tracked via storefronts.ts's
  // `replacedFrom` field and already documented in the Whatmore compare
  // page (lib/copy/competitors.ts, slug 'whatmore', migrations.brands).
  {
    source: 'shopify',
    brand: 'Ritualistic',
    region: 'India',
    duration: '7 months using the app',
    date: 'September 4, 2026',
    rating: 5,
    title: 'Great video plugin with a simple and effective setup',
    quote:
      "Setup was straightforward and we got the video feature running without needing much technical help. The videos integrate into the storefront without making it feel cluttered or slowing things down, and they've been useful for showing customers how our products actually look and are used.",
    pull: 'The videos integrate into the storefront without making it feel cluttered or slowing down the user experience.',
  },
  {
    source: 'shopify',
    brand: 'Wear Your Opinion',
    region: 'India',
    duration: '3 months using the app',
    date: 'April 1, 2026',
    rating: 5,
    quote:
      "We've had a great experience using Idukki for integrating live UGC on our WYO website. The platform is extremely user-friendly, and the entire setup and usage process has been smooth and efficient. It has helped us seamlessly showcase real-time content from our social media, adding a layer of authenticity to our product pages.",
    pull: 'Idukki is truly an all-in-one solution for brands looking to leverage UGC effectively. We would highly recommend it to any business aiming to enhance customer trust and engagement through authentic content.',
  },
  {
    source: 'shopify',
    brand: 'MOONFREEZE FOODS PRIVATE LIMITED',
    region: 'India',
    duration: 'About 1 month using the app',
    date: 'October 25, 2023',
    rating: 5,
    quote:
      "From the first interaction with Idukki, it's clear this platform is in a class of its own. It's more than just a UGC content platform on Shopify; it's a game-changer that truly revolutionizes the way businesses can leverage user-generated content.",
    pull: "All in all, Idukki is a top-tier platform that I would highly recommend for businesses looking to leverage UGC content. It's not just about what it does (which it does extremely well), but also about the team behind it, which is truly exceptional.",
  },
  {
    source: 'shopify',
    brand: 'amoshi.in',
    region: 'India',
    duration: '4 months using the app',
    date: 'August 10, 2026',
    rating: 5,
    quote:
      "Idukki has been such a useful addition to our website. We had so much UGC sitting on Instagram, and now customers can actually see it while they're shopping. Being able to see how the clothes move and look on different women makes a huge difference. Thank you, team Idukki!",
    pull: 'Being able to see how the clothes move and look on different women makes a huge difference.',
  },
  {
    source: 'g2',
    brand: 'Small-business reviewer',
    reviewer: 'Performance M.',
    role: 'D2C Growth Specialist · Small-Business (50 or fewer emp.)',
    date: 'July 7, 2026',
    rating: 4.5,
    title: 'Intuitive Platform that enhances the Customer Shopping Experience',
    quote:
      "The founders are incredibly easy to work with and are always available to help whenever needed. Their platform is intuitive, easy to use, and the widget has added a great touch to our website experience. We're grateful for their support and look forward to seeing how they continue to evolve the platform.",
    pull:
      "The platform has been a great experience overall. As it continues to evolve, we'd love to see even more opportunities to optimize performance and introduce additional sections. Looking forward to seeing how the platform grows in the coming months.",
  },
  {
    source: 'g2',
    brand: 'Enterprise reviewer',
    reviewer: 'TEBOGO M.',
    role: 'CRO Specialist · Enterprise (> 1000 emp.)',
    date: 'April 11, 2025',
    rating: 5,
    title: 'Fast, Flexible, and Focused on ROI',
    quote:
      "What I like best about Idukki is how easy it is to launch experiments and personalization campaigns without needing heavy dev support. The UI is intuitive, setup is quick, and everything is geared toward helping you move fast and see results. On top of that, their team is incredibly responsive and proactive, they don't just support you, they collaborate with you to grow your CRO strategy.",
    pull:
      "While workarounds and custom setups are possible, having more built-in connectors would make things even smoother. That said, the Idukki team is super receptive to feedback and always improving the platform.",
  },
  // The four attributed testimonials surfaced on idukki.io homepage
  // (May 2026). Photos hosted on the brand's WordPress CDN; using
  // direct URLs because the local mirror in lib/copy/brand.ts did
  // not capture these portrait files.
  {
    source: 'idukki',
    brand: 'Independent',
    reviewer: 'Austin Beveridge',
    role: 'Head of Marketing',
    date: 'November 1, 2024',
    rating: 5,
    quote: 'Super cool product! UGC content is definitely the future.',
    pull: 'Super cool product! UGC content is definitely the future.',
    photo: '/brand/homepage-live/testimonial-austin-beveridge.webp',
  },
  {
    source: 'idukki',
    brand: 'Independent',
    reviewer: 'Axel Borry',
    role: 'Head of E-commerce',
    date: 'November 1, 2024',
    rating: 5,
    quote: 'Great Product. Especially when there are a lot of agencies that now are fully specialized in UGC. That can make save a lot of money for every brand. Well done.',
    pull: 'A lot of agencies are now fully specialised in UGC. This can save a lot of money for every brand.',
    photo: '/brand/homepage-live/testimonial-axel-borry.webp',
  },
  {
    source: 'idukki',
    brand: 'Independent',
    reviewer: 'Nacho Franchini',
    role: 'Digital Marketing Head',
    date: 'November 1, 2024',
    rating: 5,
    quote: 'Mouth-to-mouth networking effect is still, and will keep being, the best marketing resource. Congratulations on your launch. As a marketer myself, I know how hard it is to keep this on track and pushing forward with it. Keep up the good work!',
    pull: 'Mouth-to-mouth networking is still, and will keep being, the best marketing resource.',
    photo: '/brand/homepage-live/testimonial-nacho-franchini.webp',
  },
  {
    source: 'idukki',
    brand: 'PromptBros.ai',
    reviewer: 'João Melo Cabrita',
    role: 'Co-Founder PromptBros.ai, ex-Sainsbury\'s, Retail Expert',
    date: 'April 1, 2024',
    rating: 5,
    quote: 'Idukki\'s unique CMS blends user-generated and brand content, revolutionising multi-channel brand awareness and personalisation. It boosts social media engagement and simplifies shopping with tagging and one-click add-to-basket, making it essential for innovative e-commerce strategies.',
    pull: 'Idukki\'s unique CMS blends user-generated and brand content, essential for innovative e-commerce strategies.',
    photo: '/brand/homepage-live/testimonial-joao-cabrita.jpeg',
  },
  // Theater (fashion D2C). From the published Idukki case study, attributed
  // to Gurbir, Head of Ecommerce. Backed by an A/B test (30K+ per arm):
  // +20% conversion, +25% add-to-cart, 5M+ impressions in 4 months. See
  // case-studies.ts slug 'theater'.
  {
    source: 'idukki',
    brand: 'Theater',
    reviewer: 'Gurbir',
    role: 'Head of Ecommerce, Theater',
    region: 'India',
    date: 'June 1, 2025',
    rating: 5,
    quote:
      'By surfacing real people using and styling our products, we significantly improved conversions on PDPs and across the marketing funnel.',
    pull: 'By surfacing real people using and styling our products, we significantly improved conversions on PDPs and across the marketing funnel.',
  },
]

/**
 * The only real AggregateRating this site has: the mean of the rated,
 * verbatim testimonials above. Use this everywhere a JSON-LD
 * aggregateRating is emitted instead of a placeholder number, per the
 * content-integrity rule against synthesising review counts.
 */
export function realAggregateRating(): { value: string; reviewCount: number } {
  const rated = customerTestimonials.filter((t) => typeof t.rating === 'number')
  const value = rated.length === 0
    ? '5.0'
    : (rated.reduce((sum, t) => sum + (t.rating ?? 0), 0) / rated.length).toFixed(1)
  return { value, reviewCount: customerTestimonials.length }
}

/** Helper that returns initials for the avatar tile when no photo. */
export function testimonialInitials(t: CustomerTestimonial): string {
  const source = t.reviewer ?? t.brand
  const parts = source.replace(/[^A-Za-z\s]/g, '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return '··'
}

/** Brand-name → customer-logo URL match. Searches the brand.customerLogos
 *  registry by URL filename for a token-overlap with the testimonial's
 *  brand name. Returns null when no defensible match exists, so the
 *  initials avatar takes over.
 *
 *  Currently zero matches against the existing four shopify/g2
 *  testimonial brands (COSSET / WYO / MOONFREEZE / Enterprise reviewer)
 *  because their logos were not on the live homepage logo-strip
 *  Idukki imported from. The function is wired so any future logos
 *  added to brand.customerLogos pick up automatically. */
import { brand } from './brand'

const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'of', 'for', 'co', 'ltd', 'limited', 'inc', 'llc',
  'corp', 'company', 'private', 'pvt', 'foods', 'india', 'us', 'uk',
])

function nameTokens(s: string): string[] {
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter((t) => t.length > 2 && !STOP_WORDS.has(t))
}

// Hand-added customer logos in public/brand/customer-logos/. These
// are named files dropped in directly (not scraped from WP), so they
// aren't in brand.customerLogos. We check this map first; if no
// match, fall through to the token-based scrape lookup below.
// Only include entries whose file is actually committed under
// public/brand/customer-logos/. A missing file would render a broken
// <img> in place of the cleaner initials fallback; let unmatched
// brands stay on initials until their logo lands in the repo.
const NAMED_LOGOS: Array<{ tokens: string[]; ref: string }> = [
  { tokens: ['cosset'],           ref: '/brand/customer-logos/cossetclothing.png' },
  { tokens: ['wyo', 'wear', 'opinion'], ref: '/brand/customer-logos/wyo.png' },
  { tokens: ['gokyo'],            ref: '/brand/customer-logos/gokyo.svg' },
  { tokens: ['ritualistic'],      ref: '/brand/customer-logos/ritualistic.png' },
  { tokens: ['sterling'],         ref: '/brand/customer-logos/sterlingholidays.png' },
  { tokens: ['vredevogel'],       ref: '/brand/customer-logos/vredevogel.webp' },
  { tokens: ['mallewear', 'malle'], ref: '/brand/customer-logos/mallewear.png' },
  { tokens: ['theater'],          ref: '/brand/customer-logos/theater.png' },
  { tokens: ['caistore', 'cai'],  ref: '/brand/customer-logos/caistore.jpg' },
  { tokens: ['fixmycurls', 'curls'], ref: '/brand/customer-logos/fixmycurls.webp' },
  { tokens: ['amoshi'],           ref: '/brand/customer-logos/amoshi.png' },
]

export function findCustomerLogoForBrand(brandName: string): string | null {
  const tokens = nameTokens(brandName)
  if (tokens.length === 0) return null
  // 1. Hand-added named files take precedence.
  for (const entry of NAMED_LOGOS) {
    if (entry.tokens.some((needle) => tokens.includes(needle))) return entry.ref
  }
  // 2. Fall back to the scraped WP customer-logos array.
  for (const logo of brand.customerLogos || []) {
    const haystack = (logo.sourceUrl || '').toLowerCase()
    if (tokens.some((t) => haystack.includes(t))) return logo.ref
  }
  return null
}
